import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebase';
import { auth } from './firebase';

export interface Trial {
  id?: string;
  userId: string;
  email: string;
  phone?: string; // Optionnel - ne sera pas inclus dans Firestore si undefined
  plan: 'professional';
  status: 'active' | 'expired' | 'cancelled';
  startDate: Date;
  endDate: Date;
  createdAt: Date;
  updatedAt: Date;
  cguAccepted?: boolean;
  cguAcceptedAt?: Date;
  hasHadTrial?: boolean; // Marque définitivement qu'un utilisateur a déjà eu un trial
}

class TrialService {
  private static instance: TrialService;

  static getInstance(): TrialService {
    if (!TrialService.instance) {
      TrialService.instance = new TrialService();
    }
    return TrialService.instance;
  }

  // Vérifier si un utilisateur a déjà eu un trial (basé sur userId)
  async hasUserAlreadyHadTrial(userId: string): Promise<boolean> {
    try {
      const userQuery = query(
        collection(db, 'trials'),
        where('userId', '==', userId),
        where('hasHadTrial', '==', true),
        limit(1)
      );
      const userSnapshot = await getDocs(userQuery);
      return !userSnapshot.empty;
    } catch (error) {
      console.error('Erreur lors de la vérification du trial utilisateur:', error);
      return false;
    }
  }

  // Vérifier si un email/téléphone a déjà eu un essai
  async hasAlreadyUsedTrial(email: string, phone?: string): Promise<boolean> {
    try {
      // Vérifier par email
      const emailQuery = query(
        collection(db, 'trials'),
        where('email', '==', email.toLowerCase())
      );
      const emailSnapshot = await getDocs(emailQuery);
      
      if (!emailSnapshot.empty) {
        return true;
      }

      // Vérifier par téléphone si fourni et non vide
      if (phone && phone.trim()) {
        const phoneQuery = query(
          collection(db, 'trials'),
          where('phone', '==', phone.trim())
        );
        const phoneSnapshot = await getDocs(phoneQuery);
        
        if (!phoneSnapshot.empty) {
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'essai:', error);
      return false;
    }
  }

  // Créer un nouvel essai gratuit avec acceptation des CGU
  async createTrial({
    plan,
    email,
    phone,
    trialDays = 30,
    cguAccepted = true
  }: {
    plan: 'professional';
    email: string;
    phone?: string;
    trialDays?: number;
    cguAccepted?: boolean;
  }): Promise<Trial | null> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Utilisateur non authentifié');
      }

      // Vérifier si déjà utilisé
      const hasUsedTrial = await this.hasAlreadyUsedTrial(email, phone);
      if (hasUsedTrial) {
        throw new Error('Cet email ou numéro de téléphone a déjà utilisé l\'essai gratuit');
      }

      const now = new Date();
      const endDate = new Date(now.getTime() + (trialDays * 24 * 60 * 60 * 1000));

      const trialData: Omit<Trial, 'id'> = {
        userId: currentUser.uid,
        email: email.toLowerCase(),
        plan,
        status: 'active',
        startDate: now,
        endDate,
        createdAt: now,
        updatedAt: now,
        cguAccepted,
        cguAcceptedAt: cguAccepted ? now : undefined,
        hasHadTrial: true // Marquer définitivement que cet utilisateur a eu un trial
      };

      // Ajouter le téléphone seulement s'il est fourni
      if (phone && phone.trim()) {
        (trialData as any).phone = phone.trim();
      }

      const firestoreData: any = {
        ...trialData,
        startDate: Timestamp.fromDate(trialData.startDate),
        endDate: Timestamp.fromDate(trialData.endDate),
        createdAt: Timestamp.fromDate(trialData.createdAt),
        updatedAt: Timestamp.fromDate(trialData.updatedAt)
      };

      if (trialData.cguAcceptedAt) {
        firestoreData.cguAcceptedAt = Timestamp.fromDate(trialData.cguAcceptedAt);
      }

      const docRef = await addDoc(collection(db, 'trials'), firestoreData);

      return {
        id: docRef.id,
        ...trialData
      };
    } catch (error: any) {
      console.error('Erreur lors de la création de l\'essai:', error);
      throw error;
    }
  }

  // Récupérer l'essai actuel de l'utilisateur
  async getCurrentTrial(userId: string): Promise<Trial | null> {
    try {
      const q = query(
        collection(db, 'trials'),
        where('userId', '==', userId),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc'),
        limit(1)
      );

      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      const data = doc.data();
      
      return {
        id: doc.id,
        userId: data.userId,
        email: data.email,
        phone: data.phone,
        plan: data.plan,
        status: data.status,
        startDate: data.startDate.toDate(),
        endDate: data.endDate.toDate(),
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
        cguAccepted: data.cguAccepted || false,
        cguAcceptedAt: data.cguAcceptedAt ? data.cguAcceptedAt.toDate() : undefined
      };
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'essai:', error);
      return null;
    }
  }

  // Annuler un essai
  async cancelTrial(trialId: string): Promise<boolean> {
    try {
      const trialRef = doc(db, 'trials', trialId);
      
      await updateDoc(trialRef, {
        status: 'cancelled',
        updatedAt: Timestamp.fromDate(new Date())
      });

      // Essai annulé avec succès
      return true;
    } catch (error) {
      console.error('Erreur lors de l\'annulation de l\'essai:', error);
      return false;
    }
  }

  // Vérifier si un essai est expiré et le marquer comme tel
  async checkAndUpdateExpiredTrials(): Promise<void> {
    try {
      const now = new Date();
      const q = query(
        collection(db, 'trials'),
        where('status', '==', 'active'),
        where('endDate', '<=', Timestamp.fromDate(now))
      );

      const snapshot = await getDocs(q);
      
      const updatePromises = snapshot.docs.map(docSnapshot => {
        return updateDoc(docSnapshot.ref, {
          status: 'expired',
          updatedAt: Timestamp.fromDate(now)
        });
      });

      await Promise.all(updatePromises);
      
      if (snapshot.size > 0) {
        // Essais expirés mis à jour
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour des essais expirés:', error);
    }
  }

  // Vérifier si l'utilisateur a un trial actif et a accepté les CGU
  async hasActiveTrialWithCGU(userId: string): Promise<boolean> {
    try {
      const currentTrial = await this.getCurrentTrial(userId);
      return currentTrial !== null && currentTrial.cguAccepted === true;
    } catch (error) {
      console.error('Erreur lors de la vérification du trial avec CGU:', error);
      return false;
    }
  }

  // Accepter les CGU pour un trial existant
  async acceptCGU(trialId: string): Promise<boolean> {
    try {
      const trialRef = doc(db, 'trials', trialId);
      const now = new Date();
      
      await updateDoc(trialRef, {
        cguAccepted: true,
        cguAcceptedAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now)
      });

      // CGU acceptées pour le trial
      return true;
    } catch (error) {
      console.error('Erreur lors de l\'acceptation des CGU:', error);
      return false;
    }
  }

  // Récupérer tous les essais d'un utilisateur
  async getUserTrials(userId: string): Promise<Trial[]> {
    try {
      const q = query(
        collection(db, 'trials'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          userId: data.userId,
          email: data.email,
          phone: data.phone,
          plan: data.plan,
          status: data.status,
          startDate: data.startDate.toDate(),
          endDate: data.endDate.toDate(),
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
          cguAccepted: data.cguAccepted || false,
          cguAcceptedAt: data.cguAcceptedAt ? data.cguAcceptedAt.toDate() : undefined
        };
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des essais:', error);
      return [];
    }
  }
}

export const trialService = TrialService.getInstance();
